
<template>
  <RouterLink
      :to="props.disabled || !props.url ? '' : { name: props.url, query: { _: Math.random() } }"
      class="sidenav-link"
      :class="{
      'sidenav-link--collapsed': isCollapsed,
      'sidenav-link--disabled': props.disabled,
      'sidenav-link--active': props.url && $route.name === props.url
    }"
  >
    <div
        class="sidenav-link__content"
        :class="{ 'sidenav-link__content--collapsed': isCollapsed }"
    >
      <!-- Active Indicator -->
      <div class="sidenav-link__active-indicator" v-if="props.url && $route.name === props.url"></div>

      <div
          class="sidenav-link__icon"
          :title="isCollapsed ? props.label : ''"
      >
        <slot name="icon"></slot>
      </div>

      <div class="sidenav-link__text" v-show="!isCollapsed">
        <slot name="text"></slot>
      </div>
    </div>
  </RouterLink>
</template>

<script setup>
import { ref } from "vue";
import { useRoute } from "vue-router";

const props = defineProps({
  url: {
    type: String,
    required: false,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  isCollapsed: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: ''
  }
});

const route = useRoute();
</script>

<style lang="scss" scoped>
.sidenav-link {
  color: white !important;
  padding: 0.25rem 0.5rem !important; /* Small padding */
  display: flex;
  align-items: center;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  text-decoration: none;
  position: relative;
  overflow: visible;
  margin: 0 !important;
  border: none !important;
  border-radius: 0 !important;
  outline: none !important;
  box-shadow: none !important;

  &:hover {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;

    .sidenav-link__icon {
      transform: scale(1.08);
    }
    .sidenav-link__text {
      text-decoration: underline;
    }
  }

  &:focus,
  &:active,
  &:focus-visible {
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }
}

.sidenav-link--active {
  .sidenav-link__text {
    text-decoration: underline;
    :deep(.menu-text) {
      font-weight: 600 !important;
    }
  }
  background: transparent !important;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
}

.sidenav-link--disabled {
  color: rgba(255, 255, 255, 0.4) !important;
  cursor: not-allowed;
  pointer-events: none;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;

  &:hover {
    transform: none;
    border: none !important;
    outline: none !important;
  }
}

.sidenav-link--collapsed {
  padding: 0.375rem 0 !important; /* Small collapsed padding */
  justify-content: center;
  border: none !important;
  outline: none !important;
  box-shadow: none !important;

  &.sidenav-link--active {
    background: transparent !important;
    border: none !important;
    outline: none !important;
    box-shadow: none !important;
  }
}

.sidenav-link__content {
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
}

.sidenav-link__content--collapsed {
  display: inline-block;
}

.sidenav-link__active-indicator {
  display: none;
}

.sidenav-link__icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  transition: transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.sidenav-link__text {
  margin-left: 0.5rem !important; /* Small margin */
  flex: 1;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: 0.01em;
}

.sidenav-link--collapsed .sidenav-link__icon {
  margin: 0;
}

/* Mobile responsive adjustments */
@media (max-width: 1199.98px) {
  .sidenav-link {
    padding: 0.375rem 0.625rem !important; /* Small mobile padding */
  }

  .sidenav-link__text {
    font-size: 0.9375rem;
    margin-left: 0.625rem !important;
  }
}

/* RouterLink active states */
.router-link-active,
.router-link-exact-active {
  border: none !important;
  outline: none !important;
  box-shadow: none !important;
  background-color: transparent !important;
}
</style>